import React, { useState } from 'react';
import { useCart } from '../../../../contexts/CartContext';
import { CART_CONSTANTS } from '../../../../shared/components/FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import FeaturesCart from '../FeaturesCart/FeaturesCart';
import './styles/ProductInfo.css';
import './styles/ProductInfo.mobile.css';

interface ProductVariant {
  id: string;
  size: string;
  price: number;
  compareAtPrice?: number;
  pricePerMeter: number;
  rollLength: number;
  variantImage?: string;
  variantImages?: string[];
  attributes?: Record<string, any>;
  quantity?: number;
  variantTitle?: string;
}

interface HierarchicalVariant {
  variantLevels: {
    level1: string;
    level2: string;
    level3: string;
  };
  attributes: Record<string, string>;
  sku: string;
  price: number;
  basePrice: number;
  quantity: number;
  isDefault: boolean;
  variantImages: string[];
  variantTitle?: string;
  variantDescription?: string;
  available?: boolean;
}

interface ProductInfoProps {
  productId: string;
  title: string;
  basePrice: number;
  pricePerMeter: number;
  variants: ProductVariant[];
  hierarchicalVariants?: HierarchicalVariant[];
  variantHierarchy?: string[];
  mainProductImage: string;
  compareAtPrice?: number;
  quantity?: number;
  onAddToCart: (variantId: string, quantity: number) => void;
  onBuyNow: (variantId: string, quantity: number) => void;
  onVariantChange?: (variant: any) => void;
  zoomImage?: string;
  zoomX?: number;
  zoomY?: number;
  isZoomActive?: boolean;
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  productId,
  title,
  basePrice,
  variants,
  hierarchicalVariants,
  variantHierarchy,
  mainProductImage,
  compareAtPrice,
  quantity,
  onAddToCart,
  onVariantChange,
  zoomImage,
  zoomX,
  zoomY,
  isZoomActive
}) => {
  const [selectedVariant, setSelectedVariant] = useState(variants[0]?.id || '');
  
  // Hierarchical variant state
  const [selectedLevel1, setSelectedLevel1] = useState('');
  const [selectedLevel2, setSelectedLevel2] = useState('');
  const [selectedLevel3, setSelectedLevel3] = useState('');
  const [selectedHierarchicalVariant, setSelectedHierarchicalVariant] = useState<HierarchicalVariant | null>(null);
  
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  // Check if we have hierarchical variants
  const hasHierarchicalVariants = hierarchicalVariants && hierarchicalVariants.length > 0 && variantHierarchy;
  
  const currentVariant: ProductVariant | HierarchicalVariant | null = hasHierarchicalVariants 
    ? selectedHierarchicalVariant 
    : variants.find(v => v.id === selectedVariant) || variants[0];

  // Check if product has real variants (synthetic variant has empty id)
  const isNonVariantProduct = !hasHierarchicalVariants && variants.length === 1 && variants[0]?.id === '';

  // Initialize hierarchical variants
  React.useEffect(() => {
    if (hasHierarchicalVariants) {
      const defaultVariant = hierarchicalVariants!.find(v => v.isDefault) || hierarchicalVariants![0];
      if (defaultVariant) {
        setSelectedLevel1(defaultVariant.attributes[variantHierarchy![0]] || '');
        setSelectedLevel2(defaultVariant.attributes[variantHierarchy![1]] || '');
        setSelectedLevel3(defaultVariant.attributes[variantHierarchy![2]] || '');
        setSelectedHierarchicalVariant(defaultVariant);
      }
    }
  }, [hierarchicalVariants, variantHierarchy, hasHierarchicalVariants]);

  // Check if current variant is in cart
  const cartItem = cartItems.find(item => {
    // First check if it's the same product
    if (item.productId !== productId) return false;
    
    // If non-variant product, match by checking variantSku is empty
    if (isNonVariantProduct) {
      return !item.variantSku || item.variantSku === '';
    }
    
    // For hierarchical variants, use SKU
    if (hasHierarchicalVariants && selectedHierarchicalVariant) {
      return item.variantSku === selectedHierarchicalVariant.sku;
    }
    
    // With variants, match by variantSku or variantId
    return item.variantSku === selectedVariant || item.variantId === selectedVariant;
  });
  const isInCart = !!cartItem;
  const cartQuantity = cartItem?.quantity || 0;

  const handleAddToCart = () => {
    if (hasHierarchicalVariants && selectedHierarchicalVariant) {
      onAddToCart(selectedHierarchicalVariant.sku, 1);
    } else {
      onAddToCart(selectedVariant, 1);
    }
  };

  const handleCartQuantityChange = (newQuantity: number) => {
    if (newQuantity <= 0 && cartItem) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else if (cartItem && newQuantity > 0) {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  // Hierarchical variant handlers
  const getLevel1Options = () => {
    if (!hasHierarchicalVariants) return [];
    const uniqueValues = new Set<string>();
    hierarchicalVariants!.forEach(variant => {
      const value = variant.attributes[variantHierarchy![0]];
      if (value) uniqueValues.add(value);
    });
    return Array.from(uniqueValues);
  };

  const getLevel2Options = () => {
    if (!hasHierarchicalVariants || !selectedLevel1 || !variantHierarchy || variantHierarchy.length < 2) return [];
    const uniqueValues = new Set<string>();
    hierarchicalVariants!
      .filter(variant => variant.attributes[variantHierarchy![0]] === selectedLevel1)
      .forEach(variant => {
        const value = variant.attributes[variantHierarchy![1]];
        if (value) uniqueValues.add(value);
      });
    return Array.from(uniqueValues);
  };

  const getLevel3Options = () => {
    if (!hasHierarchicalVariants || !selectedLevel1 || !selectedLevel2 || !variantHierarchy || variantHierarchy.length < 3) return [];
    const uniqueValues = new Set<string>();
    hierarchicalVariants!
      .filter(variant => 
        variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
        variant.attributes[variantHierarchy![1]] === selectedLevel2
      )
      .forEach(variant => {
        const value = variant.attributes[variantHierarchy![2]];
        if (value) uniqueValues.add(value);
      });
    return Array.from(uniqueValues);
  };

  const handleLevel1Change = (value: string) => {
    setSelectedLevel1(value);
    setSelectedLevel2('');
    setSelectedLevel3('');
    setSelectedHierarchicalVariant(null);
    
    // If this is a single-level hierarchy, find and select the variant immediately
    if (variantHierarchy && variantHierarchy.length === 1) {
      const variant = hierarchicalVariants!.find(v =>
        v.attributes[variantHierarchy![0]] === value
      );
      if (variant) {
        setSelectedHierarchicalVariant(variant);
        onVariantChange?.(variant);
      }
    } else if (variantHierarchy && variantHierarchy.length >= 2) {
      // Auto-select first available level2 option
      const level2Options = new Set<string>();
      hierarchicalVariants!
        .filter(variant => variant.attributes[variantHierarchy![0]] === value)
        .forEach(variant => {
          const levelValue = variant.attributes[variantHierarchy![1]];
          if (levelValue && variant.quantity > 0) level2Options.add(levelValue);
        });
      
      if (level2Options.size > 0) {
        const firstLevel2 = Array.from(level2Options)[0];
        setSelectedLevel2(firstLevel2);
        
        // If this is a 2-level hierarchy, find and select the variant immediately
        if (variantHierarchy.length === 2) {
          const variant = hierarchicalVariants!.find(v =>
            v.attributes[variantHierarchy![0]] === value &&
            v.attributes[variantHierarchy![1]] === firstLevel2
          );
          if (variant) {
            setSelectedHierarchicalVariant(variant);
            onVariantChange?.(variant);
          }
        } else if (variantHierarchy.length >= 3) {
          // Auto-select first available level3 option
          const level3Options = new Set<string>();
          hierarchicalVariants!
            .filter(variant => 
              variant.attributes[variantHierarchy![0]] === value &&
              variant.attributes[variantHierarchy![1]] === firstLevel2
            )
            .forEach(variant => {
              const levelValue = variant.attributes[variantHierarchy![2]];
              if (levelValue && variant.quantity > 0) level3Options.add(levelValue);
            });
          
          if (level3Options.size > 0) {
            const firstLevel3 = Array.from(level3Options)[0];
            setSelectedLevel3(firstLevel3);
            
            const variant = hierarchicalVariants!.find(v =>
              v.attributes[variantHierarchy![0]] === value &&
              v.attributes[variantHierarchy![1]] === firstLevel2 &&
              v.attributes[variantHierarchy![2]] === firstLevel3
            );
            if (variant) {
              setSelectedHierarchicalVariant(variant);
              onVariantChange?.(variant);
            }
          }
        }
      }
    }
  };

  const handleLevel2Change = (value: string) => {
    setSelectedLevel2(value);
    setSelectedLevel3('');
    setSelectedHierarchicalVariant(null);
    
    // If this is a 2-level hierarchy, find and select the variant immediately
    if (variantHierarchy && variantHierarchy.length === 2) {
      const variant = hierarchicalVariants!.find(v =>
        v.attributes[variantHierarchy![0]] === selectedLevel1 &&
        v.attributes[variantHierarchy![1]] === value
      );
      if (variant) {
        setSelectedHierarchicalVariant(variant);
        onVariantChange?.(variant);
      }
    } else if (variantHierarchy && variantHierarchy.length >= 3) {
      // Auto-select first available level3 option
      const level3Options = new Set<string>();
      hierarchicalVariants!
        .filter(variant => 
          variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
          variant.attributes[variantHierarchy![1]] === value
        )
        .forEach(variant => {
          const levelValue = variant.attributes[variantHierarchy![2]];
          if (levelValue && variant.quantity > 0) level3Options.add(levelValue);
        });
      
      if (level3Options.size > 0) {
        const firstLevel3 = Array.from(level3Options)[0];
        setSelectedLevel3(firstLevel3);
        
        const variant = hierarchicalVariants!.find(v =>
          v.attributes[variantHierarchy![0]] === selectedLevel1 &&
          v.attributes[variantHierarchy![1]] === value &&
          v.attributes[variantHierarchy![2]] === firstLevel3
        );
        if (variant) {
          setSelectedHierarchicalVariant(variant);
          onVariantChange?.(variant);
        }
      }
    }
  };

  const handleLevel3Change = (value: string) => {
    setSelectedLevel3(value);
    const variant = hierarchicalVariants!.find(v =>
      v.attributes[variantHierarchy![0]] === selectedLevel1 &&
      v.attributes[variantHierarchy![1]] === selectedLevel2 &&
      v.attributes[variantHierarchy![2]] === value
    );
    if (variant) {
      setSelectedHierarchicalVariant(variant);
      onVariantChange?.(variant);
    }
  };


  // Separate color and non-color variants - ONLY by attribute key, not by image presence
  // For non-variant products (synthetic variant with id=''), don't show any variant options
  const displayVariants = isNonVariantProduct ? [] : variants;
  
  const colorVariants = displayVariants.filter((variant) => {
    return (variant as any).attributes &&
      Object.keys((variant as any).attributes).some(key =>
        key.toLowerCase().includes('color') || key.toLowerCase().includes('colour'));
  });
  const nonColorVariants = displayVariants.filter((variant) => !colorVariants.includes(variant));

  // Detect mobile breakpoint using state to avoid SSR issues and desktop impact
  const [isMobile, setIsMobile] = useState(false);
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const showZoom = isZoomActive && zoomImage;

  // Get current variant title and price
  const getDisplayTitle = () => {
    if (hasHierarchicalVariants && selectedHierarchicalVariant) {
      return selectedHierarchicalVariant.variantTitle || title;
    }
    
    // For non-hierarchical variants, check if the current variant has a custom title
    if (!hasHierarchicalVariants && currentVariant && 'variantTitle' in currentVariant) {
      return (currentVariant as ProductVariant).variantTitle || title;
    }
    
    return title;
  };

  return (
    <div className="product-info">
      <h1 className="product-title">{getDisplayTitle()}</h1>
      <div className="price-section">
        {(() => {
          const variantCompare = !hasHierarchicalVariants && currentVariant && 'compareAtPrice' in currentVariant
            ? (currentVariant as ProductVariant).compareAtPrice 
            : undefined;
          const productCompare = compareAtPrice;
          const effectiveCompare = variantCompare || productCompare;
          const currentPrice = hasHierarchicalVariants 
            ? (selectedHierarchicalVariant?.price || basePrice)
            : (currentVariant && 'price' in currentVariant ? currentVariant.price : basePrice);
          
          return effectiveCompare && effectiveCompare > currentPrice ? (
            <>
              <span className="compare-price">₹ {Math.round(effectiveCompare)}</span>
              <span className="main-price">₹ {Math.round(currentPrice)}</span>
            </>
          ) : (
            <span className="main-price">₹ {Math.round(currentPrice)}</span>
          );
        })()}
      </div>
      {quantity !== undefined && (
        <div className="stock-status">
          {(() => {
            const availableQuantity = hasHierarchicalVariants 
              ? (selectedHierarchicalVariant?.quantity || 0)
              : quantity;
            
            if (availableQuantity === 0) {
              return <span className="out-of-stock">Out of Stock</span>;
            } else if (availableQuantity < 5) {
              return <span className="running-out">Running out - order fast!</span>;
            }
            return null;
          })()}
        </div>
      )}
      {showZoom && (
        <div className="product-info-zoom-wrapper">
          <div
            className="product-info-zoom-box"
            style={{
              backgroundImage: `url(${zoomImage || mainProductImage})`,
              backgroundPosition: `${zoomX ?? 50}% ${zoomY ?? 50}%`
            }}
          />
        </div>
      )}
      <div className="variant-section">
        {isNonVariantProduct ? (
          // Non-variant product: show only FeaturesCart design in place of variant options
          <FeaturesCart />
        ) : hasHierarchicalVariants ? (
          // Three-level hierarchical variant selector
          <>
            <div className="variant-header">
              <h3 className="variant-label">Select Options</h3>
            </div>
            
            {/* Level 1 - Coil Size */}
            <div className="variant-level">
              <h4 className="variant-level-title">{variantHierarchy![0]}</h4>
              <div className="variant-options">
                {getLevel1Options().map((option) => {
                  // Check if this option has any available variants
                  const hasAvailableVariants = hierarchicalVariants!.some(variant => 
                    variant.attributes[variantHierarchy![0]] === option && variant.quantity > 0
                  );
                  
                  return (
                    <button
                      key={option}
                      className={`variant-option ${selectedLevel1 === option ? 'selected' : ''} ${!hasAvailableVariants ? 'variant-option-disabled' : ''}`}
                      onClick={() => hasAvailableVariants && handleLevel1Change(option)}
                      disabled={!hasAvailableVariants}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Level 2 - Wire Thickness */}
            {selectedLevel1 && variantHierarchy && variantHierarchy.length >= 2 && getLevel2Options().length > 0 && (
              <div className="variant-level">
                <h4 className="variant-level-title">{variantHierarchy![1]}</h4>
                <div className="variant-options">
                  {getLevel2Options().map((option) => {
                    // Check if this level2 option has any available variants with current level1
                    // For 2-level hierarchy, check the specific variant; for 3+ levels, check any sub-variants
                    const hasAvailableVariants = variantHierarchy && variantHierarchy.length === 2
                      ? hierarchicalVariants!.some(variant => 
                          variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
                          variant.attributes[variantHierarchy![1]] === option && 
                          variant.quantity > 0
                        )
                      : hierarchicalVariants!.some(variant => 
                          variant.attributes[variantHierarchy![0]] === selectedLevel1 &&
                          variant.attributes[variantHierarchy![1]] === option && 
                          variant.quantity > 0
                        );
                    
                    return (
                      <button
                        key={option}
                        className={`variant-option ${selectedLevel2 === option ? 'selected' : ''} ${!hasAvailableVariants ? 'variant-option-disabled' : ''}`}
                        onClick={() => hasAvailableVariants && handleLevel2Change(option)}
                        disabled={!hasAvailableVariants}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Level 3 - Colour */}
            {selectedLevel1 && selectedLevel2 && variantHierarchy && variantHierarchy.length >= 3 && getLevel3Options().length > 0 && (
              <div className="variant-level">
                <h4 className="variant-level-title">{variantHierarchy![2]}</h4>
                <div className="variant-options">
                  {getLevel3Options().map((option) => {
                    // Check if this specific variant combination is available
                    const variant = hierarchicalVariants!.find(v =>
                      v.attributes[variantHierarchy![0]] === selectedLevel1 &&
                      v.attributes[variantHierarchy![1]] === selectedLevel2 &&
                      v.attributes[variantHierarchy![2]] === option
                    );
                    const hasQuantity = variant && variant.quantity > 0;
                    
                    return (
                      <button
                        key={option}
                        className={`variant-option ${selectedLevel3 === option ? 'selected' : ''} ${!hasQuantity ? 'variant-option-disabled' : ''}`}
                        onClick={() => hasQuantity && handleLevel3Change(option)}
                        disabled={!hasQuantity}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <FeaturesCart />
          </>
        ) : (
          // Legacy variant handling
          <>
            {!isNonVariantProduct && variants.length > 0 && (
              <div className="variant-header">
                <h3 className="variant-label">Select Variant</h3>
              </div>
            )}
            {/* Color Variants */}
            {colorVariants.length > 0 && (
              <div className="variant-options">
                {colorVariants.map((variant, idx) => {
                  let displayImage = (variant as any).variantImages?.[0] || (variant as any).variantImage;
                  if (idx === 0) {
                    displayImage = mainProductImage;
                  } else if (!displayImage || displayImage.trim() === '') {
                    displayImage = '/api/placeholder/80/80';
                  }
                  
                  const hasQuantity = variant.quantity === undefined || variant.quantity > 0;
                  
                  return (
                    <div
                      key={variant.id}
                      className={`variant-option variant-color ${selectedVariant === variant.id ? 'selected' : ''} ${!hasQuantity ? 'variant-option-disabled' : ''}`}
                      onClick={() => {
                        if (hasQuantity) {
                          setSelectedVariant(variant.id);
                          onVariantChange?.(variant);
                        }
                      }}
                    >
                      <div className="variant-image-container">
                        <img 
                          src={displayImage} 
                          alt={variant.size}
                          className="variant-image"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Non-Color Variants */}
            {nonColorVariants.length > 0 && (
              <div className={isMobile && nonColorVariants.length > 5 ? "variant-options-slider" : "variant-options"}>
                {nonColorVariants.map((variant) => {
                  const hasQuantity = variant.quantity === undefined || variant.quantity > 0;
                  
                  return (
                    <div
                      key={variant.id}
                      className={`variant-option variant-size-option ${selectedVariant === variant.id ? 'selected' : ''} ${!hasQuantity ? 'variant-option-disabled' : ''}`}
                      onClick={() => {
                        if (hasQuantity) {
                          setSelectedVariant(variant.id);
                          onVariantChange?.(variant);
                        }
                      }}
                    >
                      <div className="size-variant-content">
                        <div className="size-variant-header">{variant.size}</div>
                        <div className="size-variant-body">
                          <div className="size-variant-price">₹ {Math.round(variant.price)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Features section under variants */}
            <FeaturesCart />
          </>
        )}
      </div>
      <div className="quantity-section">
        <div className="action-buttons">
          <div className="price-display">
            <div className="price-amount">
              ₹ {Math.round(
                hasHierarchicalVariants 
                  ? (selectedHierarchicalVariant?.price || basePrice)
                  : (currentVariant?.price || basePrice)
              )}
            </div>
            <div className="price-note">Inc of all taxes.</div>
          </div>
          {isInCart ? (
            <div className="cart-quantity-controls">
              <button
                className="cart-quantity-btn"
                onClick={() => handleCartQuantityChange(cartQuantity - 1)}
              >
                -
              </button>
              <span className="cart-quantity-display">{cartQuantity}</span>
              <button
                className="cart-quantity-btn"
                onClick={() => {
                  if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
                    handleCartQuantityChange(cartQuantity + 1);
                  }
                }}
                disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
              >
                +
              </button>
            </div>
          ) : (
            <button
              className="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={
                hasHierarchicalVariants
                  ? (selectedHierarchicalVariant?.quantity === 0)
                  : (quantity === 0)
              }
              style={
                (hasHierarchicalVariants ? (selectedHierarchicalVariant?.quantity === 0) : (quantity === 0))
                  ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed' } 
                  : {}
              }
            >
              Add to Cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
